import { AddressBook, AddressType, OrderHistory, ProductType, UserOrderDetails } from './order-history.interface';
import { formattedJoin, parse } from '../../common/utils/util';
import { getCartItemFormat } from '../../common/utils/cart-util';
import {
  getImageSrcUrl,
  mapDurationFromExtensionAttributes,
  mapEditionOrRelease,
  mapFormatDescription,
} from '../../common/utils/product-util';
import { calculateDaysFromDate } from '../../common/utils/date-utils';
import { CONNECT_PLUS_SHARPEN_FORMAT } from './utils/order-history.util';
import { mapCreateCoverImages, mapCreateTermsToDuration } from '../create-custom/mappings/create-custom.mapping';

export const mapOrderHistoryShopResponse = (orderHistoryResponse: any[]): OrderHistory[] => {
  return orderHistoryResponse.map((order): OrderHistory => ({
    ...order,
    orderId: order.orderId ?? 0,
    paymentAdditionalInfo: order.paymentAdditionalInfo ?? {},
    userOrderDetails: order.userOrderDetails.map((item: any): UserOrderDetails => ({
      ...item,
      isDiscountedBundle: item.isDiscountedBundle ?? false,
      originalPrice: item.originalPrice ?? item.price,
      amountRefunded: item.amountRefunded ?? 0,
      promoDiscount: item.promoDiscount ?? 0,
      remainingDays: item.remainingDays ?? 0,
      freeTrialInfo: item.freeTrialInfo,
    })),
  }));
};

export const mapOrderHistoryMagentoResponse = (orderHistoryResponse): OrderHistory[] => {
  return orderHistoryResponse.map((order): OrderHistory => ({
    webOrderNo: order.increment_id.toString(),
    orderId: order.entity_id,
    orderType: order.extension_attributes?.order_type || order.order_type,
    tax: order.base_tax_amount - (order.extension_attributes?.digital_handling_fees || 0),
    freightCharges: order.base_shipping_amount,
    totalPrice: order.grand_total,
    totalRefunded: order.total_refunded,
    subTotal: order.items.reduce((acc, product) => acc + extractItemPriceForSubtotal(product) * product.qty_ordered, 0),
    bundleDiscount: order.items.reduce((acc, product) => acc + extractBundleDiscountFromProduct(product) * product.qty_ordered, 0),
    orderedDate: order.created_at,
    overAllOrderStatus: order.status,
    metaOfferId: order.extension_attributes?.meta_offer_id,
    couponCode: order.coupon_code,
    address: _billingAndShippingAddress(order),
    userOrderDetails: order.items.map((item): UserOrderDetails => ({
      ...item,
      ...(item.extension_attributes?.application && { application: item.extension_attributes?.application }),
      productType: item.is_virtual == 1 ? 'EBOOK' : 'SHIPPABLE',
      productIsbn: item.sku,
      itemQuantity: item.qty_ordered,
      productDescription: item.name,
      price: item.price,
      originalPrice: item.original_price,
      isDiscountedBundle: isDiscountedBundlePrice(item),
      lineStatus: order.status,
      createTime: item.created_at,
      formatDescription: getFormat(item),
      formatTypeDescription: item.extension_attributes?.format_type_desc,
      isbn10: item.extension_attributes?.isbn10,
      isbn13: item.extension_attributes?.isbn13,
      mhid: item.extension_attributes?.mhid,
      copyrightYear: item.extension_attributes?.copyright,
      grades: item.extension_attributes?.grade,
      publicationDate: item.extension_attributes?.publish_date,
      edition: item.extension_attributes?.edition,
      editionOrRelease: mapEditionOrRelease(item.extension_attributes),
      coverImage: getImageSrcUrl(item.extension_attributes?.isbn13 || item.extension_attributes?.isbn10),
      authors: formattedJoin(item.extension_attributes?.author_data.map((author: any) => author.display_name)),
      virtual: item.is_virtual == 1,
      productSegment: item.extension_attributes?.business_unit === "K12" ? "SEG" : item.extension_attributes?.business_unit,
      lineItemStatus: order.status,
      isEvergreen: item?.extension_attributes?.is_evergreen,
      productBrandingCode: item.extension_attributes?.offer_branding,
      itemType: item.extension_attributes?.item_type,
      launchObject: item.extension_attributes?.launch_object,
      offerType: item.extension_attributes?.offer_type,
      flow: item.extension_attributes?.flow,
      programSourceId: item.extension_attributes?.program_source_id ? item.extension_attributes?.program_source_id[0] : "",
      programSourceName: item.extension_attributes?.program_source_name,
      displayCode: item.extension_attributes?.display_code,
      accessCode: item.extension_attributes?.access_code,
      freeTrialInfo: {
        isFreeTrial: item.extension_attributes?.free_trial,
        startDate: item.extension_attributes?.trial_start_date,
        endDate: item.extension_attributes?.trial_end_date,
        subscriptionAmount: item.extension_attributes?.subscription_amount,
        subscriptionUnitPrice: item.extension_attributes?.subscription_unit_price,
        subscriptionTax: item.extension_attributes?.subscription_tax,
        subscriptionDigitalServiceFees: item.extension_attributes?.subscription_digital_service_fees,
        status: item.extension_attributes?.trial_status,
        cancelDate: item.extension_attributes?.trial_canceled_at,
        purchaseOrderNumber: item.extension_attributes?.purchase_order_number
      },
      isConnectSharpenBundle: item.extension_attributes?.is_sharpen,
      isSharpenCompanion: item.extension_attributes?.is_sharpen_companion,
      isSharpenAdvantage: item.extension_attributes?.is_sharpen_advantage,
      licenseStartDate: item.extension_attributes?.license_start_date,
      licenseEndDate: item.extension_attributes?.license_end_date,
      amountRefunded: item.amount_refunded,
      boardsBeyondUrl: item.extension_attributes?.boardsbeyond_url,
      businessUnit: item.extension_attributes?.business_unit,
      createCustomProductMetadata: {
        courses: parse(item.extension_attributes?.course_info) || [],
        createCoverImages: mapCreateCoverImages(item.extension_attributes?.cover_images),
        deliveryTypes: parse(item.extension_attributes?.delivery_type) || [],
        department: item.extension_attributes?.department,
        instructorNames: parse(item.extension_attributes?.instructor_name) || [],
        school: parse(item.extension_attributes?.school),
        supplements: parse(item.extension_attributes?.supplements) || [],
      },
      ...mapDuration(item.extension_attributes, order.created_at),
    })),
    totalPromoDiscount: Math.abs(order.base_discount_amount),
    country: order.billing_address.country_id,
    currencyCode: order.order_currency_code,
    orderSegment: order.extension_attributes?.business_unit === "K12" ? "SEG" : order?.extension_attributes?.business_unit,
    digitalHandlingFees: order.extension_attributes?.digital_handling_fees || 0,
    paymentAdditionalInfo: order.extension_attributes.payment_additional_info.reduce(
      (obj, item) => Object.assign(obj, { [item.key]: item.value }), {}
    ),
    paymentType: order.extension_attributes.payment_additional_info.find(function (item) {
      return item.key == 'method_title';
    }).value,
    fundingSource: order.extension_attributes.payment_additional_info.find(function (item) {
      return item.key == 'fundingSource';
    })?.value,
    formatDescription: getFormat(order.items?.[0]),
    orderStatusText: order.status,
    purchaseOrderNumber: order.payment?.po_number,
    totalLineItems: order.items.length,
    totalProductCount: order.items.length,
    returnInfo: {
      isAutoReturnable: order.extension_attributes?.return_info?.is_auto_returnable,
      eligibleThrough: order.extension_attributes?.return_info?.eligible_through,
      returnThrough: order.extension_attributes?.return_info?.return_through,
      status: order.extension_attributes?.return_info?.return_status,
      returnCondition: order.extension_attributes?.return_info?.return_condition,
      returnReason: order.extension_attributes?.return_info?.return_reason,
      caseNumber: order.extension_attributes?.return_info?.return_case_number,
      returnType: order.extension_attributes?.return_info?.return_case,
      dateRequested: order.extension_attributes?.return_info?.date_requested,
      dateCompleted: order.extension_attributes?.return_info?.date_completed,
    },
  }));
}

// The shop orders endpoint used by instructor My Account already returns an order-history-like shape,
// so it cannot reuse the raw Magento mapper above that expects increment_id/entity_id/items payloads.
export const mapShopSampleOrderResponse = (orders: any[]): OrderHistory[] => {
  return (orders || []).map((order) => ({
    webOrderNo: order.webOrderNo,
    orderId: order.orderId || 0,
    orderType: order.orderType,
    userId: order.userId,
    tax: order.tax || 0,
    freightCharges: order.freightCharges || 0,
    totalPrice: order.totalPrice || 0,
    subTotal: order.subTotal || 0,
    bundleDiscount: order.bundleDiscount || 0,
    orderedDate: order.orderedDate,
    overAllOrderStatus: order.overAllOrderStatus,
    address: order.address || [],
    userOrderDetails: (order.userOrderDetails || []).map((item: any): UserOrderDetails => ({
      ...item,
      productType: item.virtual ? ProductType.VIRTUAL_EBOOK : ProductType.SHIPPABLE,
      productIsbn: item.productIsbn,
      itemQuantity: item.itemQuantity || 1,
      productDescription: item.productDescription || item.productIsbn,
      price: item.price || 0,
      originalPrice: item.originalPrice || 0,
      isDiscountedBundle: !!item.isDiscountedBundle,
      accessCode: item.accessCode || '',
      lineStatus: item.lineStatus,
      createTime: item.createTime,
      processedQty: item.processedQty || 0,
      emailSent: item.emailSent || 0,
      edition: item.edition || '',
      editionOrRelease: item.editionOrRelease,
      formatCode: item.formatCode || '',
      formatDescription: item.formatDescription || '',
      formatTypeDescription: item.formatTypeDescription,
      authors: item.authors || '',
      publicationDate: item.publicationDate || '',
      isbn10: item.isbn10 || '',
      isbn13: item.isbn13 || '',
      source: item.source || '',
      coverImage: item.coverImage || '',
      grades: item.grades,
      virtual: !!item.virtual,
      productBrandingCode: item.productBrandingCode || item.category || '',
      mghControlledProduct: !!item.mghControlledProduct,
      available: item.available !== false,
      displayCode: !!item.displayCode,
      offerId: item.offerId || 0,
      offerPolicy: item.offerPolicy || '',
      promoDiscountValue: item.promoDiscountValue || 0,
      promoDiscount: item.promoDiscount || 0,
      mhid: item.mhid || '',
      productSegment: item.productSegment || 'HE',
      copyrightYear: item.copyrightYear || 0,
      entOfferId: item.entOfferId || 0,
      remainingDays: item.remainingDays || 0,
      lineItemStatus: item.lineItemStatus,
      terms: item.terms || '',
      offerType: item.offerType,
      itemType: item.itemType,
      flow: item.flow,
      launchObject: item.launchObject,
      programSourceId: item.programSourceId,
      programSourceName: item.programSourceName,
      freeTrialInfo: item.freeTrialInfo,
      isConnectSharpenBundle: !!item.isConnectSharpenBundle,
      isSharpenCompanion: !!item.isSharpenCompanion,
      isSharpenAdvantage: !!item.isSharpenAdvantage,
      licenseStartDate: item.licenseStartDate,
      licenseEndDate: item.licenseEndDate,
      boardsBeyondUrl: item.boardsBeyondUrl,
      extension_attributes: item.extension_attributes,
      amountRefunded: item.amountRefunded || 0,
      businessUnit: item.businessUnit || 'HE',
      duration: item.duration,
      durationPeriod: item.durationPeriod,
      createCustomProductMetadata: item.createCustomProductMetadata,
    })),
    totalPromoDiscount: order.totalPromoDiscount || 0,
    country: order.country || 'US',
    currencyCode: order.currencyCode || 'USD',
    orderSegment: order.orderSegment || 'HE',
    digitalHandlingFees: order.digitalHandlingFees || 0,
    paymentAdditionalInfo: order.paymentAdditionalInfo || {},
    paymentType: order.paymentType || '',
    formatDescription: order.formatDescription || order.userOrderDetails?.[0]?.formatDescription || '',
    paypalTransactionId: order.paypalTransactionId,
    purchaseOrderNumber: order.purchaseOrderNumber,
    orderStatusText: order.orderStatusText || '',
    totalLineItems: order.totalLineItems || 0,
    totalProductCount: order.totalProductCount || 0,
    billingAddress: order.billingAddress,
    shippingAddress: order.shippingAddress,
    returnInfo: order.returnInfo,
    metaOfferId: order.metaOfferId,
    totalRefunded: order.totalRefunded || 0,
  }));
}

function extractItemPriceForSubtotal(product: any) {
  if (isDiscountedBundlePrice(product)) {
    return product.original_price;
  }

  return product.price;
}

function extractBundleDiscountFromProduct(product: any) {
  if (isDiscountedBundlePrice(product)) {
    return product.original_price - product.price;
  }

  return 0;
}

function isDiscountedBundlePrice(product) {
  return (product.original_price !== product.price) && product.extension_attributes.product_offer_id;
}

function _billingAndShippingAddress(order): AddressBook[] {
  const addressBooks: AddressBook[] = [_address(order.billing_address)];
  const shippingAddress = order.extension_attributes?.shipping_assignments[0]?.shipping?.address;
  if (shippingAddress) {
    addressBooks.push(_address(shippingAddress, AddressType.SHIPPING_ADDRESS));
  }
  return addressBooks;
}

function _address(address, addressType = AddressType.BILLING_ADDRESS): AddressBook {
  return {
    street1: address.street[0],
    city: address.city,
    state: address.region,
    zipCode: address.postcode,
    country: address.country_id,
    type: addressType,
    address1: address.street[0],
    firstName: address.firstname,
    lastName: address.lastname,
    phoneNumber: address.telephone,
    phone: address.telephone
  }
}

function getFormat(item: any) {
  if (item.extension_attributes.is_sharpen || item.extension_attributes?.is_sharpen_advantage) {
    return CONNECT_PLUS_SHARPEN_FORMAT;
  }

  return getCartItemFormat(mapFormatDescription(item.extension_attributes), "", item.is_virtual)
}

function mapDuration(extensionAttributes: any, date: string) {
  const durationData = mapDurationFromExtensionAttributes(extensionAttributes);
  if (Object.keys(durationData).length === 0) {
    return {};
  }

  return {
    duration: durationData.duration,
    durationPeriod: durationData.durationPeriod,
    terms: getTerms(extensionAttributes, date),
    ...mapCreateTermsToDuration(extensionAttributes?.terms),
  };
}

function getTerms(extension_attributes: any, date: string) {
  try {
    if (extension_attributes.terms) {
      const termsObject = parse(extension_attributes.terms);
      if (termsObject?.days) {
        extension_attributes.term = String(termsObject.days);
      }
    }

    const DAY_DURATION_REGEX = /(\d+)(Day|Month|Year)?/;
    const {term, duration, duration_unit} = extension_attributes;
    
    if(term || duration) {
      if(term) {
        if (term === '-1' || term.toLowerCase() === 'always') {
          return term;
        }
        const match = term.match(DAY_DURATION_REGEX);
        const days = match[1];
        const duration = match[2] || "Day";
        return calculateDaysFromDate(days, duration, date);
      } else {
        return calculateDaysFromDate(duration, duration_unit, date);
      }
    }
    return "";
  } catch (e) {
    console.error(e);
    return "";
  }
}
